import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../hooks/useApiClient';
import { LoadingSpinner, PageError } from '@/components/ui';
import IncidentList from '../../components/IncidentList';
import IncidentDetailModal from '../../components/IncidentDetailModal';
import { Incident } from '@haccp/api-client';
import { useTranslation } from 'react-i18next';

type StatusFilter = 'ALL' | 'OPEN' | 'CLOSED';

export default function IncidentsPage() {
  const apiClient = useApiClient();
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(0);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

  const REFRESH_INTERVAL = 30 * 1000;

  const { data, isLoading, error } = useQuery({
    queryKey: ['incidents', page],
    queryFn: async () => {
      const result = await apiClient.getIncidents(page * 50, 50);
      return result;
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const filteredIncidents = data?.data?.filter((incident: Incident) => {
    if (statusFilter === 'OPEN') return incident.status === 'OPEN';
    if (statusFilter === 'CLOSED') return incident.status === 'CLOSED';
    return true;
  }) || [];

  const openCount = data?.data?.filter((i: Incident) => i.status === 'OPEN').length || 0;
  const closedCount = data?.data?.filter((i: Incident) => i.status === 'CLOSED').length || 0;

  return (
    <div className="space-y-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">{t('incidents.title')}</h1>
        <p className="text-neutral-600 mt-2">{t('incidents.subtitle')}</p>
      </div>

      {isLoading ? (
        <LoadingSpinner message={t('incidents.loadingMsg')} />
      ) : error ? (
        <PageError message={t('incidents.errorMsg')} />
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-neutral-600">{t('incidents.total')}</div>
          <div className="text-3xl font-bold text-neutral-900 mt-2">{data?.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-red-600">{t('incidents.ongoing')}</div>
          <div className="text-3xl font-bold text-red-700 mt-2">{openCount}</div>
        </div>
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="text-sm font-medium text-green-600">{t('incidents.resolved')}</div>
          <div className="text-3xl font-bold text-green-700 mt-2">{closedCount}</div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter('ALL')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'ALL'
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('incidents.all')}
        </button>
        <button
          onClick={() => setStatusFilter('OPEN')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'OPEN'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('incidents.openFilter', { count: openCount })}
        </button>
        <button
          onClick={() => setStatusFilter('CLOSED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'CLOSED'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'
          }`}
        >
          {t('incidents.closedFilter', { count: closedCount })}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200">
        <IncidentList
          incidents={filteredIncidents}
          isLoading={isLoading}
          onSelectIncident={setSelectedIncidentId}
        />
      </div>

      {data && data.total > 50 && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-neutral-600">
            {t('incidents.showing', { from: page * 50 + 1, to: Math.min((page + 1) * 50, data.total), total: data.total })}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('incidents.previous')}
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * 50 >= data.total}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('incidents.next')}
            </button>
          </div>
        </div>
      )}

      {selectedIncidentId && (
        <IncidentDetailModal
          incidentId={selectedIncidentId}
          onClose={() => setSelectedIncidentId(null)}
          onResolved={() => setSelectedIncidentId(null)}
        />
      )}
      </>
      )}
    </div>
  );
}
